import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc, Firestore } from 'firebase/firestore';
import { IconLink } from '@tabler/icons-react';

import type { Recipe, ShareLink } from '@recipe-rack/shared';

import { Button, Loader, RRLogo } from '../../design/components';
import { db } from '../Recipe/Firebase/InitialiseFirebase';

// TODO: replace with shared.getShareLink once Phase F lands
async function getShareLinkLocal(
  database: Firestore,
  token: string
): Promise<ShareLink | null> {
  const snap = await getDoc(doc(database, `shareLinks/${token}`));
  if (!snap.exists()) return null;
  return snap.data() as ShareLink;
}

function deriveDomain(link: string | undefined): string | null {
  if (!link) return null;
  try {
    return new URL(link).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Public read-only recipe view at /r/:shareToken. No auth required.
 * Reads a `shareLinks/<token>` doc which carries a full snapshot of the
 * recipe at share time.
 */
const ShareRecipe: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState<ShareLink | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!shareToken) {
      setLoading(false);
      setLink(null);
      return;
    }
    (async () => {
      try {
        const result = await getShareLinkLocal(db, shareToken);
        if (cancelled) return;
        setLink(result);
      } catch {
        if (!cancelled) setLink(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  return (
    <div className="rr-share">
      <header className="rr-share-topbar">
        <Link
          to="/"
          className="rr-share-brand"
          aria-label="Go to Recipe Rack"
        >
          <span className="rr-share-brand-mark" aria-hidden="true">
            <RRLogo size={32} />
          </span>
          <span className="rr-share-brand-wordmark">
            Recipe<span style={{ color: 'var(--tomato)' }}>·</span>Rack
          </span>
        </Link>
      </header>

      {loading ? (
        <div className="rr-share-loading">
          <Loader block label="Loading recipe…" />
        </div>
      ) : !link ? (
        <main className="rr-share-empty">
          <p className="rr-eyebrow">Not found</p>
          <h1 className="rr-display rr-share-empty-title">
            This recipe isn't here
          </h1>
          <p className="rr-share-empty-body">
            The link may have expired or never existed. If a friend sent it,
            ask them to share it again.
          </p>
          <Link to="/" className="rr-share-empty-link">
            Go to Recipe Rack
          </Link>
        </main>
      ) : (
        <SharedRecipeBody recipe={link.snapshot} />
      )}
    </div>
  );
};

const SharedRecipeBody: React.FC<{ recipe: Recipe }> = ({ recipe }) => {
  const domain = deriveDomain(recipe.link);
  return (
    <main className="rr-share-main">
      <section className="rr-share-hero">
        <p className="rr-eyebrow">Shared recipe · Read-only</p>
        <h1 className="rr-display rr-share-hero-title">{recipe.title}</h1>
        {domain && (
          <a
            href={recipe.link}
            target="_blank"
            rel="noreferrer noopener"
            className="rr-share-source rr-mono"
          >
            <IconLink size={14} aria-hidden="true" />
            <span>{domain}</span>
          </a>
        )}
      </section>

      <section className="rr-share-section">
        <h2 className="rr-share-section-title">Ingredients</h2>
        {recipe.ingredients.length === 0 ? (
          <p className="rr-share-empty-text">No ingredients listed.</p>
        ) : (
          <ul className="rr-share-ingredients">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="rr-share-ingredient">
                <span className="rr-mono rr-share-ingredient-index">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>{ing}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rr-share-section">
        <h2 className="rr-share-section-title">Method</h2>
        {recipe.method.length === 0 ? (
          <p className="rr-share-empty-text">No method steps yet.</p>
        ) : (
          <ol className="rr-share-method">
            {recipe.method.map((step, i) => (
              <li key={i} className="rr-share-method-step">
                <span className="rr-share-method-num rr-mono" aria-hidden="true">
                  {i + 1}
                </span>
                <p className="rr-share-method-text">{step}</p>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="rr-share-cta">
        <div className="rr-share-cta-card">
          <h3 className="rr-display rr-share-cta-title">
            Save to your Recipe Rack
          </h3>
          <p className="rr-share-cta-body">
            Get your own collection. Free.
          </p>
          <Link to="/">
            <Button variant="primary">Start your shelf</Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ShareRecipe;
